import { type Ref, forwardRef, useImperativeHandle } from 'react';

import { Box, Button, useMediaQuery, useTheme } from '@mui/material';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import {
  type FieldValues,
  type UseFormSetValue,
  useFormContext,
} from 'react-hook-form';

import ResponsiveActionsLayout from '@modules/reports/shared/ResponsiveActionsLayout';
import { useLocale } from '@shared/hooks/locale';

type FooterProps = {
  step: number;
  setStep: (step: number) => void;
  updating: boolean;
  hideBackButton?: boolean;
  saveLabel?: string;
};

export type FooterActionsRef = {
  setValue: UseFormSetValue<FieldValues>;
};

const FooterActionsComponentWithForm = (
  props: FooterProps,
  ref: Ref<FooterActionsRef>,
) => {
  const { step, setStep, updating, hideBackButton, saveLabel } = props;
  const { t } = useLocale();
  const { setValue } = useFormContext();
  const { breakpoints } = useTheme();
  const isTablet = useMediaQuery(breakpoints.down('md'));

  useImperativeHandle(ref, () => ({ setValue }), [setValue]);

  if (isTablet) {
    return <ResponsiveActionsLayout />;
  }

  return (
    <ResponsiveActionsLayout>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          p: 1,
        }}
      >
        {hideBackButton !== true ? (
          <Button startIcon={<ChevronLeft />} onClick={() => setStep(step - 1)}>
            {t('Back')}
          </Button>
        ) : (
          <Box />
        )}
        <Button
          loading={updating}
          endIcon={<ChevronRight />}
          loadingPosition="end"
          variant="contained"
          type="submit"
        >
          {saveLabel ?? t('Next')}
        </Button>
      </Box>
    </ResponsiveActionsLayout>
  );
};

export const FooterActionsComponentWithoutForm = (props: FooterProps) => {
  const { step, setStep, updating } = props;
  const { t } = useLocale();
  const { breakpoints } = useTheme();
  const isTablet = useMediaQuery(breakpoints.down('md'));

  const onClickHandler = async () => {
    setStep(step + 1);
  };

  if (isTablet) {
    return <ResponsiveActionsLayout />;
  }

  return (
    <div>
      <ResponsiveActionsLayout>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            p: 1,
          }}
        >
          <Button startIcon={<ChevronLeft />} onClick={() => setStep(step - 1)}>
            {t('Back')}
          </Button>
          <Button
            endIcon={<ChevronRight />}
            variant="contained"
            onClick={onClickHandler}
            loading={updating}
            loadingPosition="end"
          >
            {t('Next')}
          </Button>
        </Box>
      </ResponsiveActionsLayout>
    </div>
  );
};

export const FooterActions = forwardRef(FooterActionsComponentWithForm);
